/**
 * Client-side Firebase initialisation.
 *
 * Ported from `lib/firebase/client.ts`. **Auth is the only Firebase product used from the
 * browser** — Firestore is reached exclusively through the API, and `firestore.rules` denies
 * client access deliberately. Nothing here should ever grow a Firestore import.
 *
 * Everything is lazy: importing this module performs no I/O and needs no configuration, so a
 * test or a build that merely pulls it in does not have to stand up a Firebase project.
 */

import { getApp, getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from "firebase/app";
import {
  GoogleAuthProvider,
  connectAuthEmulator,
  getAuth,
  signInWithPopup,
  signOut,
  type Auth,
  type UserCredential,
} from "firebase/auth";

const FIREBASE_APP_NAME = "wanderlight";
const AUTH_EMULATOR_URL = "http://127.0.0.1:9099";

/**
 * Treats a present-but-empty value as absent.
 *
 * `VITE_FIREBASE_API_KEY=` with nothing after it is an empty *string*, and `"" ?? fallback`
 * keeps the empty string — which the Firebase SDK rejects with `auth/invalid-api-key` before
 * the sign-in popup ever opens. The Next.js app shipped that bug twice; the fix travels with
 * the port rather than being rediscovered.
 */
function envValue(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function isEmulatorMode(): boolean {
  return import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true";
}

function readConfig(): FirebaseOptions {
  const apiKey = envValue(import.meta.env.VITE_FIREBASE_API_KEY);
  const authDomain = envValue(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN);
  const projectId = envValue(import.meta.env.VITE_FIREBASE_PROJECT_ID);
  const appId = envValue(import.meta.env.VITE_FIREBASE_APP_ID);

  // The emulator ignores the API key but the SDK still insists on a non-empty string, so this
  // only hard-fails outside emulator mode.
  if (!isEmulatorMode() && (!apiKey || !authDomain || !projectId || !appId)) {
    throw new Error(
      "Firebase client config is incomplete. Set VITE_FIREBASE_API_KEY, " +
        "VITE_FIREBASE_AUTH_DOMAIN, VITE_FIREBASE_PROJECT_ID and VITE_FIREBASE_APP_ID — " +
        "see frontend/.env.example.",
    );
  }

  return {
    apiKey: apiKey ?? "emulator-api-key",
    authDomain: authDomain ?? "127.0.0.1",
    // **Must match the backend's project.** The emulator issues ID tokens *for a project* and
    // namespaces Firestore data by one, so a mismatch reads as "Invalid or expired token" with
    // nothing to suggest why. `scripts/dev_stack.py` passes the same id to both sides.
    projectId: projectId ?? "lensworthy-dev",
    appId: appId ?? "emulator-app-id",
  };
}

/** Lazy singleton. The `getApps()` check also survives a hot-module reload re-running this. */
export function getFirebaseApp(): FirebaseApp {
  const existing = getApps().find((app) => app.name === FIREBASE_APP_NAME);
  if (existing) return existing;
  try {
    return initializeApp(readConfig(), FIREBASE_APP_NAME);
  } catch (error) {
    // Only the duplicate-app race is recoverable: another caller created the app between the
    // check above and this call, so `getApp` now succeeds. Anything else is a real failure.
    if (error instanceof Error && error.message.includes("already exists")) {
      return getApp(FIREBASE_APP_NAME);
    }
    throw error;
  }
}

let emulatorConnected = false;

export function getFirebaseAuth(): Auth {
  const auth = getAuth(getFirebaseApp());
  if (isEmulatorMode() && !emulatorConnected) {
    // `disableWarnings` because the banner it injects otherwise covers the sign-in button in a
    // small viewport, and the emulator is already obvious from the project id on screen.
    connectAuthEmulator(auth, AUTH_EMULATOR_URL, { disableWarnings: true });
    emulatorConnected = true;
  }
  return auth;
}

export function signInWithGoogle(): Promise<UserCredential> {
  return signInWithPopup(getFirebaseAuth(), new GoogleAuthProvider());
}

export function signOutUser(): Promise<void> {
  return signOut(getFirebaseAuth());
}
